import { useEffect, useState } from 'react';

import { Box, Flex } from '@vimeo/bokeh';

import { useGetSvvManageBpEvents } from '../../bp-events';
import { useQuestionContext } from '../../contexts/QuestionContext';
import * as finalCopy from '../../copy/final';
import { useAiGenerationStore } from '../../stores/ai-generation-store';
import { Choice } from '../Choice';
import { Section, SectionLabel, SectionTitle } from '../CommonComponents/LayoutComponents';
import { PromptInput } from '../PromptInput';
import { QuickActions } from '../QuickActions';
import { ResponsiveBox } from '../ResponsiveComponents';
import { Description, TruncateTextWrapper } from './QuestionDisplay.style';

interface Props {
  onMomentSelect?: (timestamp: number) => void;
  showPrompt?: boolean;
  onAskQuestion: (question: string) => void;
}

export const QuestionDisplay = ({ onMomentSelect, onAskQuestion, showPrompt = true }: Props) => {
  const [isSectionHovered, setIsSectionHovered] = useState(false);

  const { question, answer, moments, relatedQuestions } = useQuestionContext();
  const getGenerationId = useAiGenerationStore((state) => state.getGenerationId);
  const [generationId] = useState(() => getGenerationId('ask_ai'));
  const {
    sendViewSuggestedAIAnswerEvent,
    sendSelectSuggestedAIQuestionEvent,
    sendPlayMomentClickEvent,
    sendAskAQuestionEvent,
  } = useGetSvvManageBpEvents();

  const handleSubmitFromPrompt = (question: string) => {
    sendAskAQuestionEvent(question);
    onAskQuestion(question);
  };

  useEffect(function handleComponentMount() {
    sendViewSuggestedAIAnswerEvent(question, answer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ResponsiveBox
      height="100%"
      alignItems="flex-start"
      alignSelf="stretch"
      display="flex"
      flexDirection="column"
      onMouseOver={() => setIsSectionHovered(true)}
      onMouseLeave={() => setIsSectionHovered(false)}
    >
      <Section>
        <SectionTitle>
          <SectionLabel>{question}</SectionLabel>
          <QuickActions
            showThumbs={isSectionHovered}
            ratedFeature={'ask_ai'}
            generationId={generationId}
          />
        </SectionTitle>
        <Description role="alert" aria-live="polite">
          {answer}
        </Description>
      </Section>

      {moments.length > 0 ? (
        <Section pt={0}>
          {moments.map((moment) => {
            const questionTitle = moment.quoteTitle ?? finalCopy.playMoment;

            return (
              <Choice.PlayMoment
                isPrimary={true}
                key={moment.timecode}
                onClick={() => {
                  sendPlayMomentClickEvent(moment.timecode, questionTitle);
                  onMomentSelect?.(moment.timecode);
                }}
              >
                <TruncateTextWrapper>{questionTitle}</TruncateTextWrapper>
              </Choice.PlayMoment>
            );
          })}
        </Section>
      ) : null}
      {relatedQuestions.length > 0 ? (
        <Box mt="auto" width={'100%'}>
          <Section>
            <SectionLabel>{finalCopy.related}</SectionLabel>
            <Flex flexDirection="column" gap="8px" alignSelf="stretch">
              {relatedQuestions.map((question, index) => (
                <Choice
                  key={`question-${index}`}
                  shouldWrap={true}
                  onClick={() => {
                    onAskQuestion(question);
                    sendSelectSuggestedAIQuestionEvent({ copy: question, isRelated: true });
                  }}
                >
                  {question}
                </Choice>
              ))}
              {showPrompt && <PromptInput onSubmit={handleSubmitFromPrompt} />}
            </Flex>
          </Section>
        </Box>
      ) : null}
    </ResponsiveBox>
  );
};
